import type { api } from '@goaltree/backend/convex/_generated/api'
import { cn } from '@goaltree/ui/lib/utils'
import type { useQuery } from 'convex/react'
import { Check, Crown } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { CanopyAvatar } from '@/components/canopy-avatar'
import { GroveIcon } from '@/components/groves/grove-icon'
import { JoinRequests } from '@/components/groves/grove-showcase'
import { StreakFlame } from '@/components/streak-icons'
import { UserAvatar } from '@/components/user-avatar'

export type GroveDetail = NonNullable<ReturnType<typeof useQuery<typeof api.groves.get>>>

const MEDALS = ['🥇', '🥈', '🥉']

export function GroveHeader({ grove }: { grove: GroveDetail }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-3">
      <GroveIcon icon={grove.icon} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-lg font-extrabold tracking-tight text-foreground">
          {grove.name}
        </p>
        <p className="text-xs text-muted-foreground">
          {t('groves.membersOf', { count: grove.memberCount, max: grove.maxMembers })}
        </p>
      </div>
      <GroveStreak grove={grove} />
    </div>
  )
}

export function GroveStreak({ grove, compact }: { grove: GroveDetail; compact?: boolean }) {
  const { t } = useTranslation()
  return (
    <div
      className={cn('flex shrink-0 flex-col items-center', !grove.allInToday && 'opacity-60')}
      title={t('groves.streakHint')}
    >
      <span className={cn('font-black text-foreground', compact ? 'text-sm' : 'text-xl')}>
        <StreakFlame /> {grove.groveStreak}
      </span>
      {!compact && (
        <span className="text-[10px] font-medium text-muted-foreground">
          {t('groves.streakLabel')}
        </span>
      )}
    </div>
  )
}

export function TodayProgress({ grove }: { grove: GroveDetail }) {
  const { t } = useTranslation()
  const pct = grove.memberCount ? (grove.checkedInToday / grove.memberCount) * 100 : 0
  return (
    <div className="rounded-xl border border-border/50 bg-foreground/[0.03] px-3 py-2.5">
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-foreground">
          {grove.allInToday
            ? t('groves.today.allIn')
            : t('groves.today.checkedIn', {
                done: grove.checkedInToday,
                total: grove.memberCount,
              })}
        </span>
        <span className="text-muted-foreground">
          {t('groves.today.weekTotal', { count: grove.weekTotal })}
        </span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

export function MemberList({ grove }: { grove: GroveDetail }) {
  const { t } = useTranslation()
  return (
    <section>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {t('groves.membersTitle')}
      </p>
      <div className="divide-y divide-border/40">
        {grove.members.map((m) => (
          <div key={m.userId} className="flex items-center gap-3 py-2.5">
            <div className="relative shrink-0">
              <CanopyAvatar userId={m.userId}>
                <UserAvatar
                  name={m.name}
                  image={m.image}
                  className={cn(
                    'size-9 rounded-full object-cover ring-2 ring-offset-2 ring-offset-background',
                    m.checkedInToday ? 'ring-primary' : 'ring-transparent',
                  )}
                />
              </CanopyAvatar>
              {m.checkedInToday && (
                <span className="absolute -right-1 -bottom-1 flex size-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <Check className="size-2.5" strokeWidth={3.5} />
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1 truncate text-sm font-semibold text-foreground">
                <span className="truncate">{m.isMe ? t('groves.you') : m.name}</span>
                {m.role === 'owner' && <Crown className="size-3 shrink-0 text-amber-500" />}
              </p>
              <p className="text-xs text-muted-foreground">
                {m.checkedInToday ? t('groves.today.done') : t('groves.today.notYet')}
              </p>
            </div>
            {m.streak > 0 && (
              <span className="text-sm font-bold text-foreground">
                <StreakFlame /> {m.streak}
              </span>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}

export function WeekBoard({ grove }: { grove: GroveDetail }) {
  const { t } = useTranslation()
  const top = Math.max(1, ...grove.leaderboard.map((e) => e.weekCount))
  return (
    <section>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {t('groves.weekBoard')}
      </p>
      <div className="flex flex-col gap-2">
        {grove.leaderboard.map((e) => (
          <div key={e.userId} className="flex items-center gap-2.5">
            <span className="w-5 shrink-0 text-center text-xs font-bold text-muted-foreground">
              {e.weekCount > 0 ? (MEDALS[e.rank - 1] ?? e.rank) : '–'}
            </span>
            <CanopyAvatar userId={e.userId}>
              <UserAvatar
                name={e.name}
                image={e.image}
                className="size-6 shrink-0 rounded-full object-cover"
              />
            </CanopyAvatar>
            <div className="min-w-0 flex-1">
              <p
                className={cn(
                  'truncate text-xs',
                  e.isMe ? 'font-bold text-foreground' : 'font-medium text-foreground',
                )}
              >
                {e.isMe ? t('groves.you') : e.name}
              </p>
              <div className="mt-1 h-1 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary/70"
                  style={{ width: `${(e.weekCount / top) * 100}%` }}
                />
              </div>
            </div>
            <span className="w-8 shrink-0 text-right text-sm font-black text-foreground">
              {e.weekCount}
            </span>
          </div>
        ))}
      </div>
    </section>
  )
}

export function GroveOverview({ grove }: { grove: GroveDetail }) {
  return (
    <div className="flex flex-col gap-5">
      <GroveHeader grove={grove} />
      {grove.me.role === 'owner' && <JoinRequests groveId={grove._id} />}
      {grove.description && (
        <p className="-mt-2 text-sm text-muted-foreground">{grove.description}</p>
      )}
      <TodayProgress grove={grove} />
      <MemberList grove={grove} />
      <WeekBoard grove={grove} />
    </div>
  )
}

export function OverviewSkeleton() {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <div className="size-14 shrink-0 animate-pulse rounded-2xl bg-muted/30" />
        <div className="flex-1">
          <div className="h-4 w-1/2 animate-pulse rounded bg-muted/30" />
          <div className="mt-2 h-2.5 w-1/4 animate-pulse rounded bg-muted/20" />
        </div>
      </div>
      <div className="h-14 animate-pulse rounded-xl bg-muted/20" />
      <div className="divide-y divide-border/40">
        {[50, 64, 40].map((w, i) => (
          <div key={i} className="flex items-center gap-3 py-2.5">
            <div className="size-9 shrink-0 animate-pulse rounded-full bg-muted/30" />
            <div className="flex-1">
              <div className="h-3.5 animate-pulse rounded bg-muted/30" style={{ width: `${w}%` }} />
              <div className="mt-1.5 h-2.5 w-1/4 animate-pulse rounded bg-muted/20" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
